import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { SidebarUiComponent } from '@psg/ui-common/sidebar/sidebar.ui-component';
import Keycloak from 'keycloak-js';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { Toast } from 'primeng/toast';
import { filter } from 'rxjs';
import { AppearanceService } from './domains/shared/services/appearance.service';
import { ServerErrorDialogService } from './domains/shared/services/server-error-dialog.service';
import { UserService } from './domains/shared/services/user.service';

@Component({
  standalone: true,
  selector: 'app-root',
  imports: [
    RouterOutlet,
    ButtonModule,
    RouterModule,
    ConfirmDialog,
    RouterOutlet,
    ButtonModule,
    SidebarUiComponent,
    RouterModule,
    Toast,
    Dialog,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {
  readonly activatedRoute = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly keycloak = inject(Keycloak);
  readonly serverErrorDialog = inject(ServerErrorDialogService);
  readonly appearance = inject(AppearanceService);
  // eslint-disable-next-line no-unused-private-class-members
  readonly #userService = inject(UserService);

  private readonly currentUrl = signal<string>(this.router.url);

  readonly showSidebar = computed(() => {
    const url = this.currentUrl();
    const errorPages = ['/access-denied', '/not-found'];
    return !errorPages.some((page) => url.includes(page));
  });

  constructor() {
    if (!this.keycloak.authenticated) {
      this.keycloak.login();
    }

    // Track route changes to update sidebar visibility
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
      });
  }

  ngOnInit(): void {
    // Simulate app initialization time
    setTimeout(() => {
      const loader = document.getElementById('initial-loader');
      if (loader) {
        loader.classList.add('fade-out');
        setTimeout(() => loader.remove(), 500);
      }
    }, 3500); // Adjust timing as needed
  }

  get userName(): string {
    const token = this.keycloak.tokenParsed;
    const fullName = [token?.['given_name'], token?.['family_name']].filter(Boolean).join(' ');
    return fullName || token?.['preferred_username'] || '';
  }

  logout(): void {
    this.keycloak.logout();
  }
}
